import { CognitoUser } from 'amazon-cognito-identity-js';
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useAuthenticationRepository } from '../../repository/auth/useAuthenticationRepository';
import { LoginWrapper } from '../login/LoginWrapper';

type AuthenticationContextValue = {
    currentUser: CognitoUser | null;
    logIn: (username: string, password: string) => Promise<unknown>;
    logout: () => void;
    sendForgottenPasswordLink: (email: string) => Promise<void>;
    changePassword: (email: string, confirmationCode: string, newPassword: string) => Promise<void>;
    completeNewPassword: (newPassword: string) => Promise<void>;
    getJwtToken: () => Promise<string>;
    challengeUser: CognitoUser | null;
    challengeUserAttributes: any;
    getCurrentUserSub: () => string | null;
}

const AuthenticationContext = createContext<AuthenticationContextValue | null>(null);

type AuthenticationProviderProps = {
    children: ReactNode;
}

export const AuthenticationProvider = ({ children }: AuthenticationProviderProps) => {
    const {
        signIn,
        signOut,
        sendForgottenPasswordLink,
        changePassword,
        completeNewPassword: completeNewPasswordRepo,
        getCurrentUser,
        getJwtToken,
        refreshSession
    } = useAuthenticationRepository();
    const currentUser = useMemo(() => getCurrentUser(), [getCurrentUser]);

    const [challengeUser, setChallengeUser] = useState<CognitoUser | null>(null);
    const [userAttributes, setUserAttributes] = useState<any>(null);

    useEffect(() => {
        if (currentUser) {
            refreshSession()
                .catch((e) => {
                    console.log('error while refreshing session - signing out', e);
                    logout();
                })
        }
    }, []);

    const logout = () => {
        signOut();

        window.location.href = '/';
    }

    const logIn = async (username: string, password: string) => {
        setChallengeUser(null);
        setUserAttributes(null);
        try {
            return await signIn(username, password);
        } catch (e: any) {
            if (e.code === 'NewPasswordRequired') {
                setChallengeUser(e.cognitoUser);
                setUserAttributes(e.userAttributes);
            }
            throw e;
        }
    }

    const completeNewPassword = async (newPassword: string) => {
        if (!challengeUser) {
            throw new Error('No challenge user');
        }

        const { email, phone_number, email_verified, phone_number_verified, sub, ...attributesToSubmit } = userAttributes || {};

        try {
            await completeNewPasswordRepo(challengeUser, newPassword, attributesToSubmit);
            setChallengeUser(null);
            setUserAttributes(null);
        } catch (e: any) {
            console.error('Error while completing new password challenge', e);
            if (e.code === 'NotAuthorizedException') {
                setChallengeUser(null);
                setUserAttributes(null);
            }
            throw e;
        }
    }

    const getCurrentUserSub = () => {
        if (currentUser) {
            return currentUser.getUsername();
        }
        return null;
    }

    const value = {
        currentUser,
        logIn,
        logout,
        sendForgottenPasswordLink,
        changePassword,
        completeNewPassword,
        getJwtToken,
        getCurrentUserSub,
        challengeUser,
        challengeUserAttributes: userAttributes,
    }

    return (
        <AuthenticationContext.Provider value={value}>
            {!currentUser && <LoginWrapper />}
            {currentUser && children}
        </AuthenticationContext.Provider>
    );
}

export const useAuthentication = () => {
    const context = useContext(AuthenticationContext);

    if (!context) {
        throw new Error('useAuthentication must be used within an AuthContextProvider');
    }

    return context;
}